import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Minus, Plus } from "lucide-react";
import { useCallback } from "react";

interface Props {
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  disabled: boolean;
}

export function AddCardToBinderQuantityInput({
  value,
  onChange,
  min,
  max,
  disabled,
}: Props) {
  const toast = useToast();

  const onPlus = useCallback(() => {
    const newValue = value + 1;

    if (newValue > max) {
      toast.toast({
        description: "You can add up to 20 cards at the same time.",
      });
      return;
    }

    onChange(newValue);
  }, [value, max, onChange, toast]);

  const onMinus = useCallback(() => {
    const newValue = value - 1;

    if (newValue < min) {
      return;
    }

    onChange(newValue);
  }, [onChange, value, min]);

  return (
    <div className="flex items-center gap-2">
      <Button
        variant="outline"
        size="icon"
        onClick={onMinus}
        disabled={disabled}
      >
        <Minus className="w-4 h-4" />
      </Button>
      <span
        className={cn("px-3 w-[40px] text-center", {
          "opacity-90": disabled,
        })}
      >
        {value}
      </span>
      <Button
        variant="outline"
        size="icon"
        onClick={onPlus}
        disabled={disabled}
      >
        <Plus className="w-4 h-4" />
      </Button>
    </div>
  );
}

export function AddToCardQuantitySkeleton() {
  return (
    <div className="flex items-center gap-2">
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
      <Skeleton className="h-9 w-9 rounded-md" />
    </div>
  );
}
